(function() {
  'use strict';

  class BarcodeScanner {
    constructor() {
      this.scannerButton = null;
      this.searchInput = null;
      this.video = null;
      this.stream = null;
      this.codeReader = null;
      this.overlay = null;
      this.isScanning = false;
      this._consensusLast = null;
      this._consensusCount = 0;
      this.init();
    }

    normalizeBarcode(barcode) {
      return String(barcode == null ? '' : barcode).trim();
    }

    /**
     * For GTIN-length numeric codes (8/12/13/14), require a valid check digit.
     * Returns true if valid, false if invalid, null if checksum does not apply.
     */
    isPlausibleGTIN(code) {
      if (!/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(code)) {
        return null;
      }
      const digits = code.split('').map(Number);
      let sum = 0;
      for (let i = digits.length - 2; i >= 0; i--) {
        const posFromRight = digits.length - 2 - i;
        const weight = posFromRight % 2 === 0 ? 3 : 1;
        sum += digits[i] * weight;
      }
      const check = (10 - (sum % 10)) % 10;
      return check === digits[digits.length - 1];
    }

    /**
     * Require the same decode twice in a row (and valid GTIN when applicable)
     * so random one-frame misreads do not trigger search.
     */
    proposeBarcodeScanned(barcode) {
      const code = this.normalizeBarcode(barcode);
      if (!code) {
        return;
      }

      const gtinCheck = this.isPlausibleGTIN(code);
      if (gtinCheck === false) {
        return;
      }

      if (code === this._consensusLast) {
        this._consensusCount += 1;
      } else {
        this._consensusLast = code;
        this._consensusCount = 1;
      }

      if (this._consensusCount >= 2) {
        this._consensusLast = null;
        this._consensusCount = 0;
        this.handleBarcodeScanned(code);
      }
    }

    resetScanConsensus() {
      this._consensusLast = null;
      this._consensusCount = 0;
    }

    init() {
      document.addEventListener('DOMContentLoaded', () => {
        this.scannerButton = document.querySelector('.search__barcode-btn');
        
        // Find search input - try multiple selectors
        this.searchInput = document.getElementById('search-input') || 
                          document.querySelector('input[type="search"]') ||
                          document.querySelector('.search__input input') ||
                          document.querySelector('.field__input[type="search"]');
        
        if (this.scannerButton && this.searchInput) {
          this.scannerButton.addEventListener('click', () => this.startScanning());
        }
      });
    }

    async startScanning() {
      if (this.isScanning) {
        return;
      }

      try {
        // Detect mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                         (window.innerWidth <= 768);
        
        // Request camera access with mobile-optimized constraints
        const videoConstraints = {
          facingMode: 'environment', // Use back camera on mobile
        };
        
        if (isMobile) {
          // Higher resolution on mobile for better barcode detection
          // Mobile cameras are usually good quality, so we can use higher res
          videoConstraints.width = { ideal: 1280, min: 640 };
          videoConstraints.height = { ideal: 720, min: 480 };
        } else {
          // Higher resolution on desktop
          videoConstraints.width = { ideal: 1280 };
          videoConstraints.height = { ideal: 720 };
        }
        
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints
        });

        this.stream = stream;
        this.isScanning = true;
        this.isMobile = isMobile;
        this.resetScanConsensus();
        this.nativeDetectInFlight = false;
        this.quaggaDecodeInFlight = false;
        this.createOverlay();
        this.setupScanner();
      } catch (error) {
        alert('Unable to access camera. Please ensure camera permissions are granted.');
        this.isScanning = false;
      }
    }

    createOverlay() {
      // Create overlay
      this.overlay = document.createElement('div');
      this.overlay.className = 'barcode-scanner-overlay';
      this.overlay.style.zIndex = '999999'; // Ensure it's above everything
      
      // Close overlay when clicking outside video
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.stopScanning();
        }
      });
      
      // Create video container
      const videoContainer = document.createElement('div');
      videoContainer.style.position = 'relative';
      videoContainer.style.width = '100%';
      videoContainer.style.maxWidth = '600px';
      videoContainer.style.display = 'flex';
      videoContainer.style.flexDirection = 'column';
      videoContainer.style.alignItems = 'center';
      
      // Prevent clicks on container from closing overlay
      videoContainer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      
      // Create video element
      this.video = document.createElement('video');
      this.video.className = 'barcode-scanner-video';
      this.video.setAttribute('autoplay', '');
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('muted', ''); // Required for autoplay in many browsers
      this.video.srcObject = this.stream;
      this.video.style.width = '100%';
      this.video.style.height = 'auto';
      
      // Mobile-specific video optimizations
      if (this.isMobile) {
        this.video.setAttribute('webkit-playsinline', '');
        this.video.setAttribute('x5-playsinline', ''); // For some Android browsers
      }
      
      // Create status message
      const status = document.createElement('div');
      status.className = 'barcode-scanner-status';
      status.textContent = 'Point camera at barcode';
      this.status = status; // Store reference for updateStatus method
      
      // Create close button
      const closeBtn = document.createElement('button');
      closeBtn.className = 'barcode-scanner-close';
      closeBtn.textContent = 'Close';
      closeBtn.addEventListener('click', () => this.stopScanning());
      
      // Create controls container
      const controls = document.createElement('div');
      controls.className = 'barcode-scanner-controls';
      controls.appendChild(closeBtn);
      
      // Assemble overlay
      videoContainer.appendChild(this.video);
      videoContainer.appendChild(status);
      videoContainer.appendChild(controls);
      this.overlay.appendChild(videoContainer);
      
      // Append to body, not inside search container, to avoid stacking context issues
      document.body.appendChild(this.overlay);
      
      // Temporarily remove blur filter from body children when scanner is open
      // The search overlay applies blur to body > * children, so we need to prevent that
      if (document.body.classList.contains('navbar--show-search')) {
        // Find all blurred children and temporarily remove blur
        const blurredElements = Array.from(document.body.children).filter(el => {
          const computedStyle = window.getComputedStyle(el);
          return computedStyle.filter && computedStyle.filter !== 'none' && 
                 !el.classList.contains('barcode-scanner-overlay') &&
                 !el.classList.contains('cart-drawer');
        });
        
        blurredElements.forEach(el => {
          el.dataset.originalFilter = el.style.filter || '';
          el.style.filter = 'none';
        });
        
        this.blurredElements = blurredElements;
      }
      
      // Play video and wait for it to be ready
      // On mobile, ensure video plays properly
      const playPromise = this.video.play();
      
      if (playPromise !== undefined) {
          playPromise
          .then(() => {
            // Ensure video is actually playing on mobile
            if (this.isMobile && this.video.paused) {
              setTimeout(() => {
                this.video.play().catch(() => {
                  // Video play failed, continue anyway
                });
              }, 100);
            }
          })
          .catch(() => {
            this.updateStatus('Error starting camera. Please try again.');
          });
      }
    }

    setupScanner() {
      // Wait for video to be ready and playing
      const startWhenReady = () => {
        // On mobile, wait a bit longer for video to stabilize
        const readyCheck = () => {
          if (this.video.readyState >= 2 && this.video.videoWidth > 0 && !this.video.paused) {
            this.startScanningLoop();
          } else {
            setTimeout(readyCheck, 100);
          }
        };
        
        readyCheck();
      };

      this.video.addEventListener('loadedmetadata', startWhenReady);
      this.video.addEventListener('playing', startWhenReady);
      this.video.addEventListener('canplay', startWhenReady);
      
      // Also try immediately in case video is already ready
      if (this.video.readyState >= 2 && this.video.videoWidth > 0 && !this.video.paused) {
        setTimeout(() => {
          this.startScanningLoop();
        }, this.isMobile ? 500 : 100);
      }
    }

    startScanningLoop() {
      // First, try native BarcodeDetector API if available (Chrome/Edge)
      if ('BarcodeDetector' in window) {
        this.useNativeDetector = true;
        this.lastScanTime = 0;
        this.scanCount = 0;
        this.updateStatus('Scanning... Point camera at barcode');
        this.scanFrame();
        return;
      }
      
      // Try QuaggaJS as alternative
      let checkCount = 0;
      const maxChecks = 100; // Wait up to 10 seconds (100 * 100ms)
      
      const checkQuagga = () => {
        checkCount++;
        
        // Check for QuaggaJS
        if (typeof window.Quagga !== 'undefined') {
          this.useQuagga = true;
          this.useNativeDetector = false;
          this.lastScanTime = 0;
          this.scanCount = 0;
          this.updateStatus('Scanning... Point camera at barcode');
          this.scanWithQuagga();
          return;
        }
        
        if (checkCount >= maxChecks) {
          this.updateStatus('Scanner library not loading. Please refresh the page.');
          return;
        }
        
        setTimeout(checkQuagga, 100);
      };
      
      checkQuagga();
    }
    
    scanWithQuagga() {
      if (!window.Quagga) {
        return;
      }
      
      // Wait for video to be ready before starting to scan
      const waitForVideo = () => {
        if (!this.video || this.video.videoWidth === 0) {
          setTimeout(waitForVideo, 100);
          return;
        }
        
        this.scanFrameWithQuagga();
      };
      
      waitForVideo();
    }
    
    scanFrameWithQuagga() {
      if (!this.isScanning || !this.video || !window.Quagga) {
        return;
      }
      
      const scanInterval = this.isMobile ? 400 : 300;
      const now = Date.now();
      if (now - this.lastScanTime < scanInterval) {
        requestAnimationFrame(() => this.scanFrameWithQuagga());
        return;
      }
      this.lastScanTime = now;
      this.scanCount = (this.scanCount || 0) + 1;
      
      if (this.video.readyState !== this.video.HAVE_ENOUGH_DATA || !this.video.videoWidth) {
        requestAnimationFrame(() => this.scanFrameWithQuagga());
        return;
      }
      
      try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = this.video.videoWidth;
        canvas.height = this.video.videoHeight;
        context.drawImage(this.video, 0, 0, canvas.width, canvas.height);
        
        const imageData = canvas.toDataURL('image/png');
        
        let quaggaInFlightTimeout = null;
        try {
          if (this.quaggaDecodeInFlight) {
            requestAnimationFrame(() => this.scanFrameWithQuagga());
            return;
          }
          this.quaggaDecodeInFlight = true;
          quaggaInFlightTimeout = setTimeout(() => {
            this.quaggaDecodeInFlight = false;
          }, 2500);
          Quagga.decodeSingle({
            decoder: {
              readers: ["ean_reader", "ean_8_reader", "code_128_reader", "code_39_reader", "codabar_reader", "upc_reader", "upc_e_reader"]
            },
            locate: true,
            src: imageData
          }, (result) => {
            clearTimeout(quaggaInFlightTimeout);
            this.quaggaDecodeInFlight = false;
            if (result && result.codeResult && result.codeResult.code) {
              this.proposeBarcodeScanned(result.codeResult.code);
            }
          });
        } catch (decodeError) {
          clearTimeout(quaggaInFlightTimeout);
          this.quaggaDecodeInFlight = false;
          // Continue scanning
        }
      } catch (error) {
        // Continue scanning
      }
      
      requestAnimationFrame(() => this.scanFrameWithQuagga());
    }

    scanFrame() {
      if (!this.isScanning || !this.video) {
        return;
      }

      // Need either native detector or ZXing reader
      if (!this.useNativeDetector && !this.codeReader) {
        return;
      }

      // Throttle scanning - slower on mobile for better performance
      const scanInterval = this.isMobile ? 400 : 300; // 400ms on mobile, 300ms on desktop
      const now = Date.now();
      if (now - this.lastScanTime < scanInterval) {
        requestAnimationFrame(() => this.scanFrame());
        return;
      }
      this.lastScanTime = now;
      this.scanCount = (this.scanCount || 0) + 1;

      // Check if video is ready
      if (this.video.readyState !== this.video.HAVE_ENOUGH_DATA) {
        requestAnimationFrame(() => this.scanFrame());
        return;
      }

      // Check if video has valid dimensions
      if (!this.video.videoWidth || !this.video.videoHeight) {
        requestAnimationFrame(() => this.scanFrame());
        return;
      }

      try {
        // Create canvas and draw video frame
        // On mobile, use smaller canvas for better performance
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        
        // Scale down canvas on mobile for better performance
        // But keep it large enough for barcode detection (at least 800px width)
        let scale = 1;
        if (this.isMobile) {
          // Use at least 800px width on mobile for better barcode detection
          // but cap at 1280px to avoid performance issues
          const targetWidth = Math.min(Math.max(800, this.video.videoWidth), 1280);
          scale = targetWidth / this.video.videoWidth;
        }
        
        canvas.width = Math.floor(this.video.videoWidth * scale);
        canvas.height = Math.floor(this.video.videoHeight * scale);
        
        // Ensure minimum dimensions for barcode detection
        if (canvas.width < 640) {
          const minScale = 640 / this.video.videoWidth;
          canvas.width = 640;
          canvas.height = Math.floor(this.video.videoHeight * minScale);
        }
        
        // Draw the video frame
        context.drawImage(this.video, 0, 0, canvas.width, canvas.height);
        
        // Use native BarcodeDetector if available
        if (this.useNativeDetector && window.BarcodeDetector) {
          this.scanWithNativeDetector(canvas);
        } else if (this.codeReader) {
          this.scanWithZXing(canvas, context);
        }
      } catch (error) {
        // Continue scanning
      }

      // Update status every 10 scans on mobile, 50 on desktop
      const updateInterval = this.isMobile ? 10 : 50;
      if (this.scanCount % updateInterval === 0) {
        const method = this.useNativeDetector ? 'Native' : 'ZXing';
        this.updateStatus(`Scanning... (${this.scanCount} frames, ${method})`);
      }

      // Continue scanning
      requestAnimationFrame(() => this.scanFrame());
    }

    async scanWithNativeDetector(canvas) {
      if (this.nativeDetectInFlight) {
        return;
      }
      this.nativeDetectInFlight = true;
      try {
        if (!this.barcodeDetector) {
          this.barcodeDetector = new BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'codabar', 'itf']
          });
        }

        const barcodes = await this.barcodeDetector.detect(canvas);

        if (barcodes && barcodes.length > 0) {
          const barcode = barcodes[0].rawValue;
          if (barcode && barcode.length > 0) {
            this.proposeBarcodeScanned(barcode);
          }
        }
      } catch (error) {
        // Continue scanning
      } finally {
        this.nativeDetectInFlight = false;
      }
    }

    scanWithZXing(canvas, context) {
      // ZXing BrowserMultiFormatReader needs ImageBitmap or ImageData
      try {
        // Method 1: Try decodeFromImageData with ImageData (most reliable)
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        try {
          const result = this.codeReader.decodeFromImageData(imageData);
          
          if (result) {
            let barcode = null;
            
            if (typeof result === 'string') {
              barcode = result;
            } else if (result.getText) {
              barcode = result.getText();
            } else if (result.text) {
              barcode = result.text;
            }
            
            if (barcode && barcode.length > 0) {
              this.proposeBarcodeScanned(barcode);
              return;
            }
          }
        } catch (e1) {
          // NotFoundException, ChecksumException, FormatException are expected
          
          // Method 2: Try creating ImageBitmap and decode (for browsers that support it)
          if (window.createImageBitmap && !this.triedImageBitmap) {
            this.triedImageBitmap = true;
            createImageBitmap(canvas).then(bitmap => {
              try {
                if (this.codeReader.decodeFromImageBitmap) {
                  const result = this.codeReader.decodeFromImageBitmap(bitmap);
                  if (result) {
                    let barcode = null;
                    if (typeof result === 'string') {
                      barcode = result;
                    } else if (result.getText) {
                      barcode = result.getText();
                    } else if (result.text) {
                      barcode = result.text;
                    }
                    
                    if (barcode && barcode.length > 0) {
                      this.proposeBarcodeScanned(barcode);
                    }
                  }
                }
              } catch (err) {
                // Continue scanning
              }
            }).catch(() => {
              // Continue scanning
            });
          }
          
          // Method 3: Try decodeFromImageElement (fallback)
          if (this.codeReader.decodeFromImageElement && !this.triedImageElement) {
            this.triedImageElement = true;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            // Use a timeout to prevent hanging
            const timeout = setTimeout(() => {
              img.onload = null;
              img.onerror = null;
            }, 1000);
            
            img.onload = () => {
              clearTimeout(timeout);
              try {
                const result = this.codeReader.decodeFromImageElement(img);
                if (result) {
                  let barcode = null;
                  if (typeof result === 'string') {
                    barcode = result;
                  } else if (result.getText) {
                    barcode = result.getText();
                  } else if (result.text) {
                    barcode = result.text;
                  }
                  
                  if (barcode && barcode.length > 0) {
                    this.proposeBarcodeScanned(barcode);
                  }
                }
              } catch (err) {
                // Continue scanning
              }
            };
            
            img.onerror = () => {
              clearTimeout(timeout);
            };
            
            img.src = canvas.toDataURL('image/png');
          }
        }
      } catch (decodeError) {
        // NotFoundException, ChecksumException, FormatException are expected when no barcode is found
        // Continue scanning
      }
    }

    updateStatus(message) {
      if (this.status) {
        this.status.textContent = message;
      }
    }

    handleBarcodeScanned(barcode) {
      // Stop scanning
      this.stopScanning();
      
      // Set barcode value in search input
      if (this.searchInput) {
        this.searchInput.value = barcode;
        
        // Trigger input event to trigger search
        const inputEvent = new Event('input', { bubbles: true });
        this.searchInput.dispatchEvent(inputEvent);
        
        // Focus the input
        this.searchInput.focus();
      }
    }

    stopScanning() {
      this.isScanning = false;
      
      // Stop QuaggaJS if it's running
      if (this.useQuagga && window.Quagga) {
        try {
          Quagga.stop();
        } catch (e) {
          // Ignore errors when stopping
        }
        this.useQuagga = false;
      }
      
      // Restore blur filters if they were removed
      if (this.blurredElements && this.blurredElements.length > 0) {
        this.blurredElements.forEach(el => {
          if (el.dataset.originalFilter) {
            el.style.filter = el.dataset.originalFilter;
            delete el.dataset.originalFilter;
          } else {
            el.style.filter = '';
          }
        });
        this.blurredElements = null;
      }
      
      // Stop code reader
      if (this.codeReader) {
        try {
          this.codeReader.reset();
        } catch (e) {
          // Ignore errors when resetting
        }
        this.codeReader = null;
      }
      
      // Stop video stream
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      
      // Remove overlay
      if (this.overlay) {
        this.overlay.remove();
        this.overlay = null;
      }
      
      this.video = null;
      this.status = null;
    }
  }

  // Initialize scanner when script loads
  new BarcodeScanner();
})();

